import { Observable, distinctUntilChanged, map } from 'rxjs';

import { Injectable, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { RouteFacade } from '@mitsubishi/core';
import { CurrencyUtils, Study, StudySubcategory, StudyUnit } from '@mitsubishi/shared/data-access';

import { Store } from '@ngxs/store';

import { StudyUnitDto } from '../../models/study-next.model';
import { CachedStudyUnit, ProjectStudiesStore } from '../../stores/project-studies.store';
import { StudyNextTranslator } from '../../utils/study-next.translator';
import { ProjectSelectors } from '../project/project.selectors';
import { StudyReadSource } from './study-read-source';

/**
 * The `StudyReadSource` backing for the new study page: `ProjectStudiesStore`, hydrated by the route
 * resolvers (`projectResolver`, `studyDetailResolver`, `studyUnitResolver`, `interimStudyResolver`) and
 * kept fresh by the project's JSON patches. The store holds transport shapes; this class is where they
 * become the legacy models the shared consumers are typed against.
 *
 * Project details stay in the NGXS tree in both flag states — only the studies leave it — so the
 * certification gate reads the backend's boolean from there (P1-CONSUMERS.md D2).
 */
@Injectable({ providedIn: 'root' })
export class StoreStudyReadSource implements StudyReadSource {
  readonly #studies = inject(ProjectStudiesStore);
  readonly #route = inject(RouteFacade);
  readonly #ngxs = inject(Store);

  readonly #routeStudyId = computed<string | undefined>(() => this.#route.routeParamsSignal()?.['studyId']);
  readonly #routeUnitId = computed<string | undefined>(() => this.#route.routeParamsSignal()?.['unitId']);

  /**
   * Every study held, in the legacy shape, adapted from its detail. A temporary study is among them: it is
   * a study (contract R11) and reaches `detailById` through the same `GET …/studies/{studyId}` as a
   * permanent one, which is why there is no separate slice for it.
   */
  readonly #studiesById = computed<Record<string, Study>>(() => {
    const studies: Record<string, Study> = {};
    for (const [studyId, detail] of Object.entries(this.#studies.detailById())) {
      studies[studyId] = StudyNextTranslator.toLegacyStudy(detail);
    }
    return studies;
  });
  readonly #studiesById$ = toObservable(this.#studiesById);
  readonly #unitById$ = toObservable(this.#studies.unitById);

  readonly #activeStudy = computed(() => {
    const studyId = this.#routeStudyId();
    return studyId ? this.#studiesById()[studyId] : undefined;
  });

  readonly #activeUnit = computed(() => {
    const unitId = this.#routeUnitId();
    return unitId ? findUnit(this.#studies.unitById(), unitId) : undefined;
  });

  readonly studySummaries = this.#studies.studySummaries;
  readonly activeStudy$ = toObservable(this.#activeStudy);
  readonly isActiveStudyTemporary = computed(() => this.#activeStudy()?.isTemporary ?? false);
  readonly activeStudyCurrencySymbol = computed(() =>
    CurrencyUtils.getCurrencySymbolFromCode(this.#activeStudy()?.currencyCode)
  );
  readonly currentStudyOrganizationId = computed(() => this.#activeStudy()?.organizationId);
  readonly activeUnit$ = toObservable(this.#activeUnit);
  readonly selectedUnit$ = this.activeUnit$;

  /** The backend's boolean (D2). Absent until the backend ships it — and an absent gate stays closed. */
  readonly canUpdateProjectCertificationOrUnitMeasures$: Observable<boolean> = this.#ngxs
    .select(ProjectSelectors.projectDetails)
    .pipe(map(details => details?.canUpdateProjectCertificationOrUnitMeasures ?? false));

  get activeStudy(): Study | undefined {
    return this.#activeStudy();
  }

  get isActiveStudyCertified(): boolean {
    return this.#activeStudy()?.certified ?? false;
  }

  getStudyById(studyId: string): Study | undefined {
    return this.#studiesById()[studyId];
  }

  studyById$(studyId: string): Observable<Study | undefined> {
    return this.#studiesById$.pipe(
      map(studies => studies[studyId]),
      distinctUntilChanged()
    );
  }

  /** The unit's own subcategory, for a unit fetched by its id (`studyUnitResolver`). */
  getSubcategoryByUnitId(unitId: string): StudySubcategory | undefined {
    const held = this.#studies.unitById()[unitId];
    return held ? StudyNextTranslator.toLegacySubcategory(held.subcategory, [held.unit]) : undefined;
  }

  /** Also answers for an external unit never fetched by its own id: its pairing carries the uuid (contract 0.8.0). */
  getSubcategoryIdByUnitId(unitId: string): string | undefined {
    const units = this.#studies.unitById();
    return units[unitId]?.subcategory.uuid ?? inlinedExternal(units, unitId)?.subcategoryUuid ?? undefined;
  }

  getUnit(unitId: string | undefined): StudyUnit | undefined {
    return unitId ? findUnit(this.#studies.unitById(), unitId) : undefined;
  }

  getExternalUnit(unitId: string | undefined, workingConditionId: string): StudyUnit | undefined {
    return unitId ? externalUnitOf(this.#studies.unitById(), unitId, workingConditionId) : undefined;
  }

  externalUnitById$(unitId: string, workingConditionId: string): Observable<StudyUnit | undefined> {
    return this.#unitById$.pipe(
      map(units => externalUnitOf(units, unitId, workingConditionId)),
      distinctUntilChanged()
    );
  }
}

/** A held unit by id, or an external unit reached through the unit it is paired with. */
function findUnit(units: Record<string, CachedStudyUnit>, unitId: string): StudyUnit | undefined {
  const dto = units[unitId]?.unit ?? inlinedExternal(units, unitId);
  return dto ? StudyNextTranslator.toLegacyUnit(dto) : undefined;
}

/** The external unit paired with a held unit on one working condition (contract 0.5.0 inlines it). */
function externalUnitOf(
  units: Record<string, CachedStudyUnit>,
  unitId: string,
  workingConditionId: string
): StudyUnit | undefined {
  const external = units[unitId]?.unit.workingConditionsData[workingConditionId]?.externalUnit;
  return external ? StudyNextTranslator.toLegacyUnit(external) : undefined;
}

function inlinedExternal(units: Record<string, CachedStudyUnit>, unitId: string): StudyUnitDto | undefined {
  for (const held of Object.values(units)) {
    for (const data of Object.values(held.unit.workingConditionsData)) {
      const external = data.externalUnit;
      if (external && external.uuid === unitId) {
        return external;
      }
    }
  }
  return undefined;
}
